import * as THREE from 'three';
import type { StarField } from './stars';
import type { GalaxyGlow } from './galaxy';

// The night sky's stars and Milky Way as seen from the eye, in a small cube map, for the water to reflect.
//
// The sky dome's own probe (sky.ts) cannot hold them: the stars read that probe's alpha to find out how much cloud is in front of them, and a
// shader cannot read the cube it is drawing into. So the same two objects (stars.ts, galaxy.ts) are drawn a second time into this cube, on their
// own layer, from the same eye, with the pixel size of a cube face and no sRGB encode (plain display values, like the dome's probe), reading the
// dome's probe for cloud as they always do. The sea and the pools (waterView.ts) look the cube up along the reflected ray at the
// mip level of the reflection's width and screen-blend it onto the sky reflection, as the stars are blended onto the sky itself.
//
// Cost: nothing by day (the stars are not drawn, nothing happens). At night, six faces of a catalogue's worth of points and the galaxy band at
// 128 x 128 pixels, once per redraw of the dome's probe (about once a second), and only while the sea is shown.

/** The layer the stars and the galaxy band are also on, for this cube's camera (the sky's probe uses 1). */
export const STAR_PROBE_LAYER = 2;
const SIZE = 128;                       // as the sky's probe (sky.ts PROBE_SIZE), so one mip level matches a reflection lobe in both
const MOVE_M = 150;                     // the eye may move this far before the cube is drawn again whatever the dome's probe does

export class StarProbe {
  readonly target = new THREE.WebGLCubeRenderTarget(SIZE, {
    type: THREE.HalfFloatType, generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter });
  private cam = new THREE.CubeCamera(1, 1e7, this.target);
  private ready = false;
  private wasOn = false;
  private lastSerial = -1;
  private readonly eyeAt = new THREE.Vector3();
  private readonly clear = new THREE.Color();
  /** True while the cube holds a current picture of a night sky: what the water should reflect. */
  active = false;

  constructor(private stars: StarField, private glow: GalaxyGlow) {
    this.cam.layers.set(STAR_PROBE_LAYER);          // sees only the stars and the band
  }

  /**
   * Once a frame, after the sky's probe (`serial` is Sky.probeSerial). `needed`: something that reflects the sky is on screen's scene (the
   * sea), so a night with no water costs nothing. Draws only when the dome's probe was redrawn, the eye has moved far, or the stars have just come up.
   */
  update(renderer: THREE.WebGLRenderer, scene: THREE.Scene, eye: THREE.Vector3, serial: number, needed: boolean) {
    const on = needed && serial > 0 && (this.stars.object.visible || this.glow.mesh.visible);
    this.active = on && this.ready;
    if (!on) { this.wasOn = false; return; }
    this.stars.object.layers.enable(STAR_PROBE_LAYER);       // (a debug toggle may have reset the layer mask)
    this.glow.mesh.layers.enable(STAR_PROBE_LAYER);
    if (this.ready && this.wasOn && serial === this.lastSerial && eye.distanceToSquared(this.eyeAt) < MOVE_M * MOVE_M) return;
    this.wasOn = true; this.lastSerial = serial; this.eyeAt.copy(eye);
    // Clear to nothing, not to the scene's background colour.
    const bg = scene.background; scene.background = null;
    renderer.getClearColor(this.clear); const ca = renderer.getClearAlpha();
    renderer.setClearColor(0x000000, 0);
    this.stars.beginProbe(SIZE); this.glow.beginProbe();
    try {
      this.cam.position.copy(eye);
      this.cam.updateMatrixWorld(true);
      this.cam.update(renderer, scene);
    } finally {
      this.stars.endProbe(); this.glow.endProbe();
      renderer.setClearColor(this.clear, ca);
      scene.background = bg;
    }
    this.ready = true;
    this.active = true;
  }

  dispose() { this.target.dispose(); }
}
